import React, { useState, useEffect } from "react";
import { useSignMessage } from "wagmi";
import { useConnection } from "@/hooks/useConnection";
import { useAppKit } from "@reown/appkit/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { verifyMessage } from "viem";
import { Wallet, ShieldCheck, AlertCircle } from "lucide-react";
import logo from "@/assets/images/logo.svg";
import { SESSION_STORAGE_KEYS } from "@/lib/storageKeys";

interface WalletGateProps {
  children: React.ReactNode;
}

// Load allowed addresses from environment variable
const getAllowedAddresses = (): Set<string> => {
  const addresses = import.meta.env.VITE_ALLOWED_ADDRESSES || "";
  return new Set(
    addresses
      .split(",")
      .map((addr: string) => addr.trim().toLowerCase())
      .filter((addr: string) => addr.length > 0),
  );
};

const ALLOWED_ADDRESSES = getAllowedAddresses();

const clearStoredWalletAuth = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.WALLET_AUTH_ADDRESS);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.WALLET_AUTH_SIGNATURE);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.WALLET_AUTH_MESSAGE);
};

export function WalletGate({ children }: WalletGateProps) {
  const { address, isConnected } = useConnection();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const { open } = useAppKit();

  const { signMessageAsync } = useSignMessage();

  // Check if authentication is already stored in session
  useEffect(() => {
    const verifyStoredAuth = async () => {
      const storedAddress = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.WALLET_AUTH_ADDRESS,
      );
      const storedSignature = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.WALLET_AUTH_SIGNATURE,
      );
      const storedMessage = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.WALLET_AUTH_MESSAGE,
      );

      // If wallet address changes, clear authentication
      if (
        address &&
        storedAddress &&
        storedAddress.toLowerCase() !== address.toLowerCase()
      ) {
        clearStoredWalletAuth();
        setIsAuthenticated(false);
        setError(null);
        return;
      }

      // If wallet is disconnected, clear authentication
      if (!address && storedAddress) {
        clearStoredWalletAuth();
        setIsAuthenticated(false);
        setError(null);
        return;
      }

      if (storedAddress && storedSignature && storedMessage && address) {
        // Verify stored signature matches current address
        if (storedAddress.toLowerCase() === address.toLowerCase()) {
          // Check if address is in allowed list (if allowlist is configured)
          if (
            ALLOWED_ADDRESSES.size > 0 &&
            !ALLOWED_ADDRESSES.has(address.toLowerCase())
          ) {
            // Address not allowed, clear storage
            clearStoredWalletAuth();
            setIsAuthenticated(false);
            return;
          }

          try {
            const isValid = await verifyMessage({
              address: address,
              message: storedMessage,
              signature: storedSignature as `0x${string}`,
            });

            if (isValid) {
              setIsAuthenticated(true);
            } else {
              // Invalid signature, clear storage
              clearStoredWalletAuth();
              setIsAuthenticated(false);
            }
          } catch (err) {
            // Invalid signature, clear storage
            clearStoredWalletAuth();
            setIsAuthenticated(false);
          }
        }
      }
    };

    verifyStoredAuth();
  }, [address]);

  const handleAuthenticate = async () => {
    if (!address) return;

    setError(null);
    setIsVerifying(true);

    try {
      // Check if address is in allowed list
      if (
        ALLOWED_ADDRESSES.size > 0 &&
        !ALLOWED_ADDRESSES.has(address.toLowerCase())
      ) {
        setError(
          "Your wallet address is not authorized to access this preview environment.",
        );
        setIsVerifying(false);
        return;
      }

      // Create message to sign
      const timestamp = Date.now();
      const message = `Sign this message to access Helm Preview\n\nAddress: ${address}\nTimestamp: ${timestamp}`;

      // Request signature
      const signature = await signMessageAsync({ message });

      // Store authentication in session
      sessionStorage.setItem(SESSION_STORAGE_KEYS.WALLET_AUTH_ADDRESS, address);
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.WALLET_AUTH_SIGNATURE,
        signature,
      );
      sessionStorage.setItem(SESSION_STORAGE_KEYS.WALLET_AUTH_MESSAGE, message);

      setIsAuthenticated(true);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("User rejected")) {
          setError("Signature request was rejected. Please try again.");
        } else {
          setError("Failed to verify wallet. Please try again.");
        }
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // If not connected, show connect prompt
  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md p-8">
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="Helm" className="h-12 w-auto mb-6" />
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <ShieldCheck className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-center">
              Preview Access Required
            </h1>
            <p className="text-muted-foreground text-center text-sm">
              Connect your wallet to access the Helm preview environment
            </p>
          </div>

          <div className="mb-6">
            <div className="bg-muted/50 border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">
                    Preview Environment
                  </p>
                  <p>
                    This is a preview deployment. You will need to sign a
                    message to verify wallet ownership.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Button onClick={() => open()} className="w-full" size="lg">
            <Wallet className="mr-2 h-5 w-5" />
            Connect Wallet
          </Button>
        </Card>
      </div>
    );
  }

  // If connected but not authenticated, show verification prompt
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <img src={logo} alt="Helm" className="h-12 w-auto mb-6" />
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Wallet className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-center">
            Verify Wallet Ownership
          </h1>
          <p className="text-muted-foreground text-center text-sm">
            Sign a message to confirm you own this wallet
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="bg-muted/50 border rounded-lg p-4">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Connected Wallet
            </div>
            <div className="font-mono text-sm break-all">{address}</div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          <div className="bg-muted/50 border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">
                  No gas fees required
                </p>
                <p>
                  Signing is free and does not submit a blockchain transaction.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Button
          onClick={handleAuthenticate}
          disabled={isVerifying}
          className="w-full"
          size="lg"
        >
          {isVerifying ? "Verifying..." : "Sign Message to Continue"}
        </Button>
      </Card>
    </div>
  );
}
